import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

// Configuración del Nivel Único Complejo (31x31 Grid)
const CELL_SIZE = 14;
const COLS = 31;
const ROWS = 31;
const GRID_START = { x: 1, y: 1 };

// Generador de Laberinto Cuadrado Complejo (DFS Algoritmo Perfecto)
function generateSquareMaze(cols, rows) {
    const grid = Array(rows).fill().map(() => Array(cols).fill(1));

    function carve(x, y) {
        const dirs = [
            [0, -2],
            [2, 0],
            [0, 2],
            [-2, 0],
        ].sort(() => Math.random() - 0.5);

        for (const [dx, dy] of dirs) {
            const nx = x + dx;
            const ny = y + dy;
            if (ny > 0 && ny < rows - 1 && nx > 0 && nx < cols - 1 && grid[ny][nx] === 1) {
                grid[ny][nx] = 0;
                grid[y + dy / 2][x + dx / 2] = 0;
                carve(nx, ny);
            }
        }
    }

    grid[1][1] = 0;
    carve(1, 1);
    grid[1][0] = 0;
    grid[rows - 2][cols - 1] = 0;

    return grid;
}

const Laberinto = () => {
    const { user } = useAuth();
    const { config } = useConfig();
    const FTP_BASE = 'https://ftp.pcnpublicidad.com/boehringer/concentrese';

    const canvasRef = useRef(null);

    // Estado del Juego (Nivel Único Dificultad Alta)
    const [gameStatus, setGameStatus] = useState('idle'); // 'idle' | 'playing' | 'won'
    const [totalTimeElapsed, setTotalTimeElapsed] = useState(0);
    const [totalMoves, setTotalMoves] = useState(0);
    const [finalScore, setFinalScore] = useState(0);
    const [pointsRegisterMsg, setPointsRegisterMsg] = useState('');

    const [maze, setMaze] = useState(() => generateSquareMaze(COLS, ROWS));
    const [player, setPlayer] = useState({ x: GRID_START.x, y: GRID_START.y });
    const [trail, setTrail] = useState([{ x: GRID_START.x, y: GRID_START.y }]);

    // Refs para evadir cierres desactualizados
    const gameStatusRef = useRef(gameStatus);
    gameStatusRef.current = gameStatus;
    const playerRef = useRef(player);
    playerRef.current = player;
    const mazeRef = useRef(maze);
    mazeRef.current = maze;

    // Formatear tiempo mm:ss
    const formatTime = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    // Iniciar Juego
    const startGame = () => {
        const newMaze = generateSquareMaze(COLS, ROWS);
        setMaze(newMaze);
        setPlayer({ x: GRID_START.x, y: GRID_START.y });
        setTrail([{ x: GRID_START.x, y: GRID_START.y }]);

        setTotalTimeElapsed(0);
        setTotalMoves(0);
        setFinalScore(0);
        setPointsRegisterMsg('');
        setGameStatus('playing');
    };

    useEffect(() => {
        startGame();
    }, []);

    // Timer global
    useEffect(() => {
        let timer = null;
        if (gameStatus === 'playing') {
            timer = setInterval(() => {
                setTotalTimeElapsed(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [gameStatus]);

    // Movimiento
    const movePlayer = (nx, ny) => {
        if (gameStatusRef.current !== 'playing') return;
        const grid = mazeRef.current;
        if (ny >= 0 && ny < ROWS && nx >= 0 && nx < COLS && grid[ny][nx] === 0) {
            setPlayer({ x: nx, y: ny });
            setTrail(prev => [...prev, { x: nx, y: ny }]);
            setTotalMoves(m => m + 1);

            if (nx === COLS - 1 && ny === ROWS - 2) {
                finishGame(totalTimeElapsed);
            }
        }
    };

    // Finalizar Juego y Evaluar Puntuación por Tiempos
    const finishGame = async (finalTime) => {
        setGameStatus('won');

        // Puntuación por Tiempo:
        // <= 40s: 40 pts
        // <= 50s: 30 pts
        // <= 60s: 20 pts
        // <= 70s: 10 pts
        // > 70s: 0 pts (Safety Car)
        let score = 0;
        if (finalTime <= 40) score = 40;
        else if (finalTime <= 50) score = 30;
        else if (finalTime <= 60) score = 20;
        else if (finalTime <= 70) score = 10;
        else score = 0;

        setFinalScore(score);

        const configuredStationId = config?.pageDesigns?.laberinto?.stationId;

        try {
            await api.post('/sessions/log', {
                page_name: 'laberinto',
                action_type: 'complete',
                details: { timeElapsed: finalTime, score }
            });

            if (configuredStationId) {
                const stationIdPayload = !isNaN(configuredStationId) ? parseInt(configuredStationId, 10) : configuredStationId;
                api.post('/points/activity-register', {
                    stationId: stationIdPayload,
                    skillPoints: score,
                    activityName: 'Laberinto'
                }).then(res => {
                    const msg = res.data?.message || res.data?.error || '¡Puntos registrados exitosamente!';
                    setPointsRegisterMsg(msg);
                }).catch(err => {
                    const backendMsg = err.response?.data?.message || err.response?.data?.error;
                    if (backendMsg) {
                        setPointsRegisterMsg(backendMsg);
                    } else {
                        setPointsRegisterMsg('Ocurrió un error al registrar los puntos.');
                    }
                });
            } else {
                setPointsRegisterMsg("Nota: No se ha asignado una estación en las configuraciones para acumular puntos.");
            }
        } catch (err) {
            console.error("Error al registrar sesión de juego", err);
        }
    };

    // Renderizado en Canvas 2D con estética Boehringer F1
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        canvas.width = COLS * CELL_SIZE;
        canvas.height = ROWS * CELL_SIZE;

        ctx.fillStyle = '#07241c';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Rastro del jugador
        if (trail.length > 1) {
            ctx.strokeStyle = 'rgba(0, 230, 118, 0.4)';
            ctx.lineWidth = CELL_SIZE / 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(trail[0].x * CELL_SIZE + CELL_SIZE / 2, trail[0].y * CELL_SIZE + CELL_SIZE / 2);
            for (let i = 1; i < trail.length; i++) {
                ctx.lineTo(trail[i].x * CELL_SIZE + CELL_SIZE / 2, trail[i].y * CELL_SIZE + CELL_SIZE / 2);
            }
            ctx.stroke();
        }

        // Paredes del Laberinto
        for (let r = 0; r < ROWS; r++) {
            for (let c = 0; c < COLS; c++) {
                if (maze[r][c] === 1) {
                    ctx.fillStyle = '#00e676';
                    ctx.shadowColor = '#00e676';
                    ctx.shadowBlur = 3;
                    ctx.fillRect(c * CELL_SIZE, r * CELL_SIZE, CELL_SIZE, CELL_SIZE);
                    ctx.shadowBlur = 0;
                }
            }
        }

        // Meta Final - Bandera a Cuadros F1 (Checkered Flag)
        const finishX = (COLS - 1) * CELL_SIZE;
        const finishY = (ROWS - 2) * CELL_SIZE;
        const halfSize = CELL_SIZE / 2;

        ctx.shadowColor = '#00e676';
        ctx.shadowBlur = 8;
        // Top-left: White, Top-right: Black
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(finishX, finishY, halfSize, halfSize);
        ctx.fillStyle = '#000000';
        ctx.fillRect(finishX + halfSize, finishY, halfSize, halfSize);
        // Bottom-left: Black, Bottom-right: White
        ctx.fillStyle = '#000000';
        ctx.fillRect(finishX, finishY + halfSize, halfSize, halfSize);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(finishX + halfSize, finishY + halfSize, halfSize, halfSize);
        ctx.shadowBlur = 0;

        // Jugador / Carro Blanco F1 (Dibujo vectorial blanco F1 exclusivo)
        const playerCenterX = player.x * CELL_SIZE + CELL_SIZE / 2;
        const playerCenterY = player.y * CELL_SIZE + CELL_SIZE / 2;
        const carWidth = CELL_SIZE * 2.8;
        const carHeight = CELL_SIZE * 1.4;

        ctx.save();
        ctx.translate(playerCenterX, playerCenterY);

        // Sombra neón blanca
        ctx.shadowColor = '#ffffff';
        ctx.shadowBlur = 6;

        // Chasis Principal Blanco
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        // Trocha central / Body
        ctx.ellipse(0, 0, carWidth * 0.4, carHeight * 0.28, 0, 0, Math.PI * 2);
        ctx.fill();

        // Nariz F1
        ctx.beginPath();
        ctx.moveTo(carWidth * 0.2, -carHeight * 0.15);
        ctx.lineTo(carWidth * 0.48, 0);
        ctx.lineTo(carWidth * 0.2, carHeight * 0.15);
        ctx.closePath();
        ctx.fill();

        // Ala Delantera
        ctx.fillRect(carWidth * 0.4, -carHeight * 0.45, carWidth * 0.08, carHeight * 0.9);

        // Ala Trasera
        ctx.fillRect(-carWidth * 0.48, -carHeight * 0.45, carWidth * 0.1, carHeight * 0.9);

        // Ruedas (Llantas F1 Blancas/Gris)
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#e2e8f0';
        // Delantera Izq
        ctx.fillRect(carWidth * 0.22, -carHeight * 0.52, carWidth * 0.16, carHeight * 0.22);
        // Delantera Der
        ctx.fillRect(carWidth * 0.22, carHeight * 0.3, carWidth * 0.16, carHeight * 0.22);
        // Trasera Izq
        ctx.fillRect(-carWidth * 0.38, -carHeight * 0.54, carWidth * 0.18, carHeight * 0.24);
        // Trasera Der
        ctx.fillRect(-carWidth * 0.38, carHeight * 0.3, carWidth * 0.18, carHeight * 0.24);

        // Cockpit / Piloto (Gris Oscuro)
        ctx.fillStyle = '#1e293b';
        ctx.beginPath();
        ctx.arc(-carWidth * 0.05, 0, carHeight * 0.16, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }, [maze, player, trail]);

    // Controles táctiles / puntero
    const [pointerActive, setPointerActive] = useState(false);

    const handlePointerMove = (e) => {
        if (!pointerActive || gameStatus !== 'playing') return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const touchX = (e.clientX - rect.left) * scaleX;
        const touchY = (e.clientY - rect.top) * scaleY;

        const currX = playerRef.current.x;
        const currY = playerRef.current.y;
        const pcx = currX * CELL_SIZE + CELL_SIZE / 2;
        const pcy = currY * CELL_SIZE + CELL_SIZE / 2;
        const dx = touchX - pcx;
        const dy = touchY - pcy;

        if (Math.abs(dx) < CELL_SIZE * 0.35 && Math.abs(dy) < CELL_SIZE * 0.35) return;

        if (Math.abs(dx) >= Math.abs(dy)) {
            movePlayer(currX + (dx > 0 ? 1 : -1), currY);
        } else {
            movePlayer(currX, currY + (dy > 0 ? 1 : -1));
        }
    };

    // Controles por Teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (gameStatusRef.current !== 'playing') return;

            const currX = playerRef.current.x;
            const currY = playerRef.current.y;
            if (e.key === 'ArrowUp') { e.preventDefault(); movePlayer(currX, currY - 1); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); movePlayer(currX, currY + 1); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); movePlayer(currX - 1, currY); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); movePlayer(currX + 1, currY); }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const [isRulesModalOpen, setIsRulesModalOpen] = useState(true);

    return (
        <div className="min-h-screen bg-[#07241c] text-white flex flex-col items-center justify-between p-4 sm:p-6 font-sans relative overflow-x-hidden">
            {/* Header del Juego */}
            <div className="w-full max-w-md text-center space-y-3 z-10 pt-2">
                <div className="flex justify-between items-center w-full">
                    <div className="inline-block bg-[#00e676]/20 border border-[#00e676]/50 text-[#00e676] px-3.5 py-1 rounded-full text-xs font-black tracking-widest uppercase shadow-md">
                        🏁 ESTACIÓN LABERINTO F1
                    </div>
                    <button
                        onClick={() => setIsRulesModalOpen(true)}
                        className="bg-[#0b382c] border border-[#00e676]/40 text-[#00e676] px-3 py-1 rounded-full text-xs font-black tracking-wider uppercase hover:bg-[#00e676] hover:text-[#07241c] transition-all shadow-md flex items-center gap-1"
                    >
                        📋 Reglas de Juego
                    </button>
                </div>

                <h1 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-white">
                    LABERINTO CORPORATIVO
                </h1>

                {/* Marcador de Tiempo */}
                <div className="flex justify-center items-center bg-[#0b382c] border border-[#00e676]/40 px-5 py-2.5 rounded-2xl shadow-lg text-xs font-bold">
                    <div>
                        Tiempo: <span className="text-[#00e676] font-mono text-base font-black ml-1">{formatTime(totalTimeElapsed)}</span>
                    </div>
                </div>
            </div>

            {/* Tablero del Laberinto en Canvas */}
            <div className="w-full max-w-md bg-[#00e676] p-3 sm:p-4 rounded-2xl shadow-2xl z-10 border-4 border-white/30 relative flex justify-center items-center overflow-hidden my-auto">
                <canvas
                    ref={canvasRef}
                    onPointerDown={(e) => {
                        e.preventDefault();
                        setPointerActive(true);
                    }}
                    onPointerMove={handlePointerMove}
                    onPointerUp={() => setPointerActive(false)}
                    onPointerCancel={() => setPointerActive(false)}
                    className="shadow-inner rounded-xl touch-none max-w-full h-auto cursor-pointer"
                />
            </div>

            {/* Modal Fin de Juego Completo */}
            {gameStatus === 'won' && createPortal(
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[99999] flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center space-y-5 shadow-2xl">
                        <div className="text-5xl">{finalScore > 0 ? '🏆' : '🚨'}</div>
                        <h2 className="text-2xl font-black text-[#00e676] uppercase tracking-wider">
                            {finalScore > 0 ? '¡CIRCUITO SUPERADO!' : 'SAFETY CAR — TIEMPO AGOTADO'}
                        </h2>
                        <p className="text-sm font-bold text-white leading-relaxed">
                            {finalScore > 0
                                ? '¡Excelente conducción! Ha completado el recorrido del laberinto.'
                                : 'Superó el límite de 70 segundos para obtener puntos de destreza.'}
                        </p>
                        <p className="text-xs font-bold text-emerald-400 bg-[#07241c] p-2.5 rounded-xl border border-[#00e676]/30">
                            💡 Al finalizar haz click en el menú y vuelve al inicio.
                        </p>

                        <div className="bg-[#07241c] p-4 rounded-2xl border border-[#00e676]/30 space-y-2">
                            <p className="text-xs text-slate-300">
                                Tiempo total: <span className="font-bold text-[#00e676]">{formatTime(totalTimeElapsed)}</span>
                            </p>
                            <p className="text-base font-black text-amber-400 pt-1">
                                ⭐ Puntos de Destreza: {finalScore} pts
                            </p>
                            {pointsRegisterMsg && (
                                <p className="text-[11px] text-[#00e676] font-bold pt-2 border-t border-[#00e676]/20">
                                    {pointsRegisterMsg}
                                </p>
                            )}
                        </div>

                        <button
                            onClick={startGame}
                            className="w-full bg-[#00e676] text-[#07241c] py-3.5 px-6 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg hover:scale-105 active:scale-95 transition-all"
                        >
                            REINICIAR DESAFÍO
                        </button>
                    </div>
                </div>,
                document.body
            )}
            {/* Modal de Reglas de Juego */}
            {isRulesModalOpen && createPortal(
                <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[99999] flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center space-y-5 shadow-2xl">
                        <div className="text-4xl">📋</div>
                        <h2 className="text-xl font-black text-[#00e676] uppercase tracking-wider">
                            REGLAS DE JUEGO & PUNTUACIÓN
                        </h2>
                        <p className="text-xs text-slate-200 leading-relaxed font-medium">
                            Conduzca el carro blanco por el laberinto hasta la bandera a cuadros en el menor tiempo posible. Los puntos de destreza se otorgan según el tiempo registrado:
                        </p>

                        <div className="bg-[#07241c] border border-[#00e676]/30 p-3 rounded-2xl text-xs font-bold text-slate-200 flex flex-col gap-2 text-left shadow-inner">
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Hasta 40 segundos:</span>
                                <span className="font-black text-[#00e676]">40 pts</span>
                            </div>
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Hasta 50 segundos:</span>
                                <span className="font-black text-[#00e676]">30 pts</span>
                            </div>
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Hasta 60 segundos:</span>
                                <span className="font-black text-[#00e676]">20 pts</span>
                            </div>
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Hasta 70 segundos:</span>
                                <span className="font-black text-amber-300">10 pts</span>
                            </div>
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-red-500/30">
                                <span>Más de 70 segundos:</span>
                                <span className="font-black text-red-400">0 pts</span>
                            </div>
                        </div>

                        <p className="text-xs font-bold text-[#00e676] bg-[#07241c] p-2.5 rounded-xl border border-[#00e676]/30">
                            💡 Al finalizar haz click en el menú y vuelve al inicio.
                        </p>

                        <button
                            onClick={() => setIsRulesModalOpen(false)}
                            className="w-full bg-[#00e676] text-[#07241c] py-3.5 px-6 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:scale-105 active:scale-95 transition-all"
                        >
                            ENTENDIDO / JUGAR
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Laberinto;
